import { useState, useEffect } from "react";
import VpsUploadField from "@/components/base/VpsUploadField";

export type InvTemplate = {
  id: string;
  tenant_id: string | null;
  name: string;
  description: string | null;
  event_types: string[];
  preview_url: string | null;
  background_url: string | null;
  overlay_color: string | null;
  text_color: string;
  text_style: string;
  font_family: string | null;
  is_active: boolean;
  is_premium: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

const EVENT_TYPE_OPTIONS = [
  "Wedding", "XV Años", "Baptism", "Graduation", "Birthday",
  "Baby Shower", "Engagement", "Anniversary", "Corporate Event", "Other",
];

const TEXT_STYLES = ["elegante", "moderno", "romantico", "mistico", "fresco", "lujoso", "clasico"];

interface Props {
  template: InvTemplate | null;
  isNew: boolean;
  tenantId: string;
  onClose: () => void;
  onSave: (data: Partial<InvTemplate>) => void;
}

const BLANK: Partial<InvTemplate> = {
  name: "",
  description: "",
  event_types: [],
  preview_url: "",
  background_url: "",
  overlay_color: "rgba(0,0,0,0.4)",
  text_color: "#ffffff",
  text_style: "elegante",
  font_family: "",
  is_active: true,
  is_premium: false,
  sort_order: 0,
};

export default function TemplateCardModal({ template, isNew, tenantId, onClose, onSave }: Props) {
  const [form, setForm] = useState<Partial<InvTemplate>>(BLANK);

  useEffect(() => {
    setForm(isNew ? BLANK : { ...template });
  }, [template, isNew]);

  const set = <K extends keyof InvTemplate>(key: K, val: InvTemplate[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const toggleEventType = (et: string) => {
    const current = (form.event_types ?? []);
    if (current.includes(et)) {
      set("event_types", current.filter((x) => x !== et));
    } else {
      set("event_types", [...current, et]);
    }
  };

  const isValid = (form.name ?? "").trim().length > 0 && (form.background_url ?? "").trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "Nuevo Template" : "Editar Template"}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">Fondo genérico para invitaciones</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Nombre <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name ?? ""}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Classic Elegant"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Descripción</label>
            <textarea
              value={form.description ?? ""}
              onChange={(e) => set("description", e.target.value)}
              rows={2}
              placeholder="Descripción breve del estilo del template…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors resize-none"
            />
          </div>

          {/* Background Upload */}
          <VpsUploadField
            value={form.background_url ?? null}
            onChange={(url) => set("background_url", url)}
            tenantId={tenantId}
            entityType="invitation_template"
            label="Fondo del Template *"
            accept="image/*"
            showPreview
            previewHeight="h-28"
            helperText="Imagen de fondo del template. Se recomienda 1080x1920px."
            folder="invitations"
            maxSizeMB={10}
          />

          {/* Preview Upload */}
          <VpsUploadField
            value={form.preview_url ?? null}
            onChange={(url) => set("preview_url", url)}
            tenantId={tenantId}
            entityType="invitation_template"
            label="Miniatura (opcional)"
            accept="image/*"
            showPreview
            previewHeight="h-28"
            helperText="Si no se sube, se usa el fondo como miniatura."
            folder="invitations"
            maxSizeMB={10}
          />

          {/* Overlay + text color */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Overlay (CSS color/rgba)</label>
              <input
                type="text"
                value={form.overlay_color ?? ""}
                onChange={(e) => set("overlay_color", e.target.value)}
                placeholder="rgba(0,0,0,0.4)"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors font-mono"
              />
            </div>
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Color de Texto</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.text_color ?? "#ffffff"}
                  onChange={(e) => set("text_color", e.target.value)}
                  className="w-9 h-9 rounded-lg border border-slate-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={form.text_color ?? "#ffffff"}
                  onChange={(e) => set("text_color", e.target.value)}
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Text style + font */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Estilo de Texto</label>
              <select
                value={form.text_style ?? "elegante"}
                onChange={(e) => set("text_style", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer"
              >
                {TEXT_STYLES.map((s) => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Fuente (Google Font)</label>
              <input
                type="text"
                value={form.font_family ?? ""}
                onChange={(e) => set("font_family", e.target.value)}
                placeholder="Cormorant Garamond"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
          </div>

          {/* Event types */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-2">Tipos de Evento Compatibles</label>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPE_OPTIONS.map((et) => {
                const active = (form.event_types ?? []).includes(et);
                return (
                  <button
                    key={et}
                    onClick={() => toggleEventType(et)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      active
                        ? "bg-[#C8A96E] border-[#C8A96E] text-white"
                        : "border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}
                  >
                    {et}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sort order + flags */}
          <div className="flex items-center gap-6 pt-1">
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Orden</label>
              <input
                type="number"
                value={form.sort_order ?? 0}
                onChange={(e) => set("sort_order", Number(e.target.value))}
                min={0}
                className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer mt-5">
              <div
                onClick={() => set("is_active", !form.is_active)}
                className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${form.is_active ? "bg-emerald-500" : "bg-slate-200"}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
              <span className="text-sm text-slate-600">Activo</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer mt-5">
              <div
                onClick={() => set("is_premium", !form.is_premium)}
                className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${form.is_premium ? "bg-[#C8A96E]" : "bg-slate-200"}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_premium ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
              <span className="text-sm text-slate-600">Premium</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            Cancelar
          </button>
          <button
            onClick={() => isValid && onSave(form)}
            disabled={!isValid}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line"></i></span>
            {isNew ? "Crear Template" : "Guardar Cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}